import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';
import {
    CheckboxesTags,
    CloseButton,
    FilterPopover,
    Loader,
    SelectActions,
    SelectAutocompletePaginationMultiple,
    SelectedFilters,
    StatusCard,
} from 'components';
import {
    clientStatusEnums,
    clientStatuses,
    FindLoad,
    Images,
    PermissionList,
    renderClientName,
    renderStaffName,
    RolePermission,
    staffStatusEnums,
    staffStatuses,
    useModal,
} from 'utils';
import {
    DEFAULT_MAP_CENTER,
    DEFAULT_MAP_ZOOM,
    distanceLabel,
    GET_MAP_LOCATIONS_ACTION,
    isPlottableCoord,
    MAP_CLUSTER_RADIUS_PX,
    MAP_COINCIDENT_SPREAD_DEG,
    MAP_COLORS,
    MAP_CONNECTOR_COLOR,
    MAP_CONNECTOR_HIT_WEIGHT,
    MAP_FILTER_KEYS,
    MAP_POLY_EVENTS,
    MAP_MARKER_TYPES,
    MAP_STYLES,
    MAX_FIT_ZOOM,
    pinsInStates,
    placeOptions,
    pruneCities,
} from './constants';
import { mapActions } from 'store';
import { AssignedClientsModal } from './assignedClientsModal';
import { bestDistance, cachedDrivingMiles, drivingMiles } from './drivingDistance';
import './clientsStaffMap.scss';

/** First+last initials, falling back to the client code so a marker is never blank. */
const initialsOf = (firstName, lastName, fallback) => {
    const a = (firstName || '').trim();
    const b = (lastName || '').trim();
    const initials = `${a ? a[0] : ''}${b ? b[0] : ''}`.toUpperCase();
    return initials || (fallback ? fallback.slice(0, 2).toUpperCase() : '?');
};

/**
 * Nudge markers that share an EXACT coordinate onto a small ring around their shared point, so a
 * group at one address behaves like any other cluster: it merges into a "+N" bubble when zoomed out
 * and separates into individual pins as you zoom in (identical points can never be split by zoom).
 * Returns new entry objects with adjusted `item.lat/lng`; singletons pass through untouched, and the
 * grouping is by exact coordinate regardless of type (so a client+staff at one point also separate).
 */
const spreadCoincidentItems = (entries) => {
    const groups = new Map();
    entries.forEach((entry) => {
        const key = `${Number(entry.item.lat)},${Number(entry.item.lng)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(entry);
    });
    const out = [];
    groups.forEach((group) => {
        if (group.length === 1) {
            out.push(group[0]);
            return;
        }
        const n = group.length;
        group.forEach((entry, i) => {
            const angle = (2 * Math.PI * i) / n - Math.PI / 2; // first pin straight up
            const lat = Number(entry.item.lat) + Math.sin(angle) * MAP_COINCIDENT_SPREAD_DEG;
            const lng = Number(entry.item.lng) + Math.cos(angle) * MAP_COINCIDENT_SPREAD_DEG;
            out.push({ ...entry, item: { ...entry.item, lat, lng } });
        });
    });
    return out;
};

/**
 * Statuses the API plots when none are picked — what the person pickers default to as well.
 * DUPLICATED from MAP_DEFAULT_STAFF_STATUSES / MAP_DEFAULT_CLIENT_STATUSES in @therapylake/package
 * (map/map.constants.ts): the client cannot import the package (see utils/constants.js). Keep in sync.
 */
const DEFAULT_STAFF_STATUSES = [staffStatusEnums.ACTIVE];
const DEFAULT_CLIENT_STATUSES = [clientStatusEnums.ACTIVE, clientStatusEnums.FBA, clientStatusEnums.ON_HOLD, clientStatusEnums.WAIT_LIST];

/** The map opens with only Active staff and Active clients picked (a real selection, not the API fallback). */
const INITIAL_FILTERS = {
    [MAP_FILTER_KEYS.STAFF_STATUSES]: staffStatuses.filter((option) => option.id === staffStatusEnums.ACTIVE),
    [MAP_FILTER_KEYS.CLIENT_STATUSES]: clientStatuses.filter((option) => option.id === clientStatusEnums.ACTIVE),
};

const idsOf = (list) => (Array.isArray(list) ? list.map((i) => i?.id ?? i) : []);
const namesOf = (list) => list.map((i) => i?.name).join(', ');
/** Status filter placeholder when nothing is picked: the statuses the API really plots, not "All". */
const defaultStatusLabel = (options, ids) => `${namesOf(options.filter((option) => ids.includes(option.id)))} (default)`;

/** Narrow a pin list by picked ids and by address state/city (an empty pick narrows nothing). */
const applyFilters = (items, ids, states, cities) =>
    items.filter(
        (i) =>
            (!ids.length || ids.includes(i.id)) &&
            (!states.length || states.includes((i.state || '').trim())) &&
            (!cities.length || cities.includes((i.city || '').trim()))
    );

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

/** Build the DOM element for an avatar pin (photo when available, otherwise initials). */
const createPinElement = ({ color, avatarUrl, initials }) => {
    const wrap = document.createElement('div');
    wrap.className = 'csm-pin';

    const bubble = document.createElement('div');
    bubble.className = 'csm-pin__bubble';
    bubble.style.borderColor = color;
    if (avatarUrl) {
        const img = document.createElement('img');
        img.src = avatarUrl;
        img.alt = '';
        // If the photo fails to load, fall back to the colored initials bubble.
        img.onerror = () => {
            img.remove();
            bubble.style.background = color;
            bubble.textContent = initials;
        };
        bubble.appendChild(img);
    } else {
        bubble.style.background = color;
        bubble.textContent = initials;
    }

    const tail = document.createElement('div');
    tail.className = 'csm-pin__tail';
    tail.style.background = color;

    wrap.appendChild(bubble);
    wrap.appendChild(tail);
    return wrap;
};

// Location pin for the info card's address row (filled #5C98EC to match the design).
const PIN_ICON =
    '<svg width="12" height="16" viewBox="0 0 9 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.05547 11.7C6.25781 10.1953 9 6.54844 9 4.5C9 2.01562 6.98438 0 4.5 0C2.01562 0 0 2.01562 0 4.5C0 6.54844 2.74219 10.1953 3.94453 11.7C4.23281 12.0586 4.76719 12.0586 5.05547 11.7ZM4.5 3C5.32787 3 6 3.67213 6 4.5C6 5.32787 5.32787 6 4.5 6C3.67213 6 3 5.32787 3 4.5C3 3.67213 3.67213 3 4.5 3Z" fill="#5C98EC"/></svg>';
// "Assigned staff" person glyph for the info card's secondary row (blue #5C98EC to match the design).
const PROVIDER_ICON =
    '<svg width="16" height="16" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.125 7C8.0567 7 9.625 5.4317 9.625 3.5C9.625 1.5683 8.0567 0 6.125 0C4.1933 0 2.625 1.5683 2.625 3.5C2.625 5.4317 4.1933 7 6.125 7ZM3.5 8.50937C1.47656 9.10273 0 10.973 0 13.1879C0 13.6363 0.363672 14 0.812109 14H11.4379C11.8863 14 12.25 13.6363 12.25 13.1879C12.25 10.973 10.7734 9.10273 8.75 8.50937V9.89844C9.50469 10.0926 10.0625 10.7789 10.0625 11.5938V12.6875C10.0625 12.9281 9.86563 13.125 9.625 13.125H9.1875C8.94687 13.125 8.75 12.9281 8.75 12.6875C8.75 12.44687 8.94687 12.25 9.1875 12.25V11.5938C9.1875 11.1098 8.79648 10.7188 8.3125 10.7188C7.8285 10.7188 7.4375 11.1098 7.4375 11.5938V12.25C7.67812 12.25 7.875 12.4469 7.875 12.6875C7.875 12.92812 7.67812 13.125 7.4375 13.125H7C6.75938 13.125 6.5625 12.9281 6.5625 12.6875V11.5938C6.5625 10.7789 7.12031 10.0926 7.875 9.89844V8.33711C7.71094 8.3207 7.54414 8.3125 7.37461 8.3125H4.87539C4.70586 8.3125 4.53906 8.3207 4.375 8.33711V10.1254C5.00664 10.3141 5.46875 10.8992 5.46875 11.5938C5.46875 12.4387 4.78242 13.125 3.9375 13.125C3.0926 13.125 2.40625 12.4387 2.40625 11.5938C2.40625 10.8992 2.86836 10.3141 3.5 10.1254V8.50937ZM3.9375 12.25C4.29969 12.25 4.59375 11.9559 4.59375 11.5938C4.59375 11.2316 4.29969 10.9375 3.9375 10.9375C3.57531 10.9375 3.28125 11.2316 3.28125 11.5938C3.28125 11.9559 3.57531 12.25 3.9375 12.25Z" fill="#5C98EC"/></svg>';

// Building glyph for the staff card's work-location row (filled #5C98EC).
const BUILDING_ICON =
    '<svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.3125 0C0.587891 0 0 0.587891 0 1.3125V12.6875C0 13.4121 0.587891 14 1.3125 14H3.9375V11.8125C3.9375 11.0879 4.52539 10.5 5.25 10.5C5.97461 10.5 6.5625 11.0879 6.5625 11.8125V14H9.1875C9.91211 14 10.5 13.4121 10.5 12.6875V1.3125C10.5 0.587891 9.91211 0 9.1875 0H1.3125ZM1.75 6.5625C1.75 6.32188 1.94687 6.125 2.1875 6.125H3.0625C3.30312 6.125 3.5 6.32188 3.5 6.5625V7.4375C3.5 7.67812 3.30312 7.875 3.0625 7.875H2.1875C1.94687 7.875 1.75 7.67812 1.75 7.4375V6.5625ZM4.8125 6.125H5.6875C5.92812 6.125 6.125 6.32188 6.125 6.5625V7.4375C6.125 7.67812 5.92812 7.875 5.6875 7.875H4.8125C4.57188 7.875 4.375 7.67812 4.375 7.4375V6.5625C4.375 6.32188 4.57188 6.125 4.8125 6.125ZM7 6.5625C7 6.32188 7.19688 6.125 7.4375 6.125H8.3125C8.55313 6.125 8.75 6.32188 8.75 6.5625V7.4375C8.75 7.67812 8.55313 7.875 8.3125 7.875H7.4375C7.19688 7.875 7 7.67812 7 7.4375V6.5625ZM2.1875 2.625H3.0625C3.30312 2.625 3.5 2.82188 3.5 3.0625V3.9375C3.5 4.17812 3.30312 4.375 3.0625 4.375H2.1875C1.94687 4.375 1.75 4.17812 1.75 3.9375V3.0625C1.75 2.82188 1.94687 2.625 2.1875 2.625ZM4.375 3.0625C4.375 2.82188 4.57188 2.625 4.8125 2.625H5.6875C5.92812 2.625 6.125 2.82188 6.125 3.0625V3.9375C6.125 4.17812 5.92812 4.375 5.6875 4.375H4.8125C4.57188 4.375 4.375 4.17812 4.375 3.9375V3.0625ZM7.4375 2.625H8.3125C8.55313 2.625 8.75 2.82188 8.75 3.0625V3.9375C8.75 4.17812 8.55313 4.375 8.3125 4.375H7.4375C7.19688 4.375 7 4.17812 7 3.9375V3.0625C7 2.82188 7.19688 2.625 7.4375 2.625Z" fill="#5C98EC"/></svg>';

// Group-of-people glyph for the staff card's "Assigned: N Clients" row (filled #5C98EC).
const GROUP_ICON =
    '<svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.9375 0C5.14481 0 6.125 0.980186 6.125 2.1875C6.125 3.39481 5.14481 4.375 3.9375 4.375C2.73019 4.375 1.75 3.39481 1.75 2.1875C1.75 0.980186 2.73019 0 3.9375 0ZM14 0C15.2073 0 16.1875 0.980186 16.1875 2.1875C16.1875 3.39481 15.2073 4.375 14 4.375C12.7927 4.375 11.8125 3.39481 11.8125 2.1875C11.8125 0.980186 12.7927 0 14 0ZM0 8.16758C0 6.55703 1.30703 5.25 2.91758 5.25H4.08516C4.51992 5.25 4.93281 5.3457 5.30469 5.51523C5.26914 5.71211 5.25273 5.91719 5.25273 6.125C5.25273 7.16953 5.71211 8.10742 6.43672 8.75C6.43125 8.75 6.42578 8.75 6.41758 8.75H0.582422C0.2625 8.75 0 8.4875 0 8.16758ZM11.0824 8.75C11.077 8.75 11.0715 8.75 11.0633 8.75C11.7906 8.10742 12.2473 7.16953 12.2473 6.125C12.2473 5.91719 12.2281 5.71484 12.1953 5.51523C12.5672 5.34297 12.9801 5.25 13.4148 5.25H14.5824C16.193 5.25 17.5 6.55703 17.5 8.16758C17.5 8.49023 17.2375 8.75 16.9176 8.75H11.0824ZM6.125 6.125C6.125 4.67622 7.30122 3.5 8.75 3.5C10.1988 3.5 11.375 4.67622 11.375 6.125C11.375 7.57378 10.1988 8.75 8.75 8.75C7.30122 8.75 6.125 7.57378 6.125 6.125ZM3.5 13.2699C3.5 11.2574 5.13242 9.625 7.14492 9.625H10.3551C12.3676 9.625 14 11.2574 14 13.2699C14 13.6719 13.6746 14 13.2699 14H4.23008C3.82812 14 3.5 13.6746 3.5 13.2699Z" fill="#5C98EC"/></svg>';

/** Up to two tag chips, then a "+N" overflow chip. */
const tagsHtml = (tags) => {
    if (!Array.isArray(tags) || tags.length === 0) return '';
    const shown = tags.slice(0, 2);
    const extra = tags.length - shown.length;
    const chip = (text, muted) =>
        `<span style="display:inline-block;padding:3px 8px;border-radius:6px;background:#F1F3F7;color:${
            muted ? '#8A90A2' : '#42526E'
        };font-size:12px;font-weight:500;line-height:1.4">${escapeHtml(text)}</span>`;
    return (
        `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:8px">` +
        shown.map((t) => chip(t, false)).join('') +
        (extra > 0 ? chip(`+${extra}`, true) : '') +
        `</div>`
    );
};

/**
 * InfoWindow card HTML for a clicked marker (matches the design). The two entity types differ:
 *   - Client: name is a link, "Client" label, pin + address, person icon + "Assigned to: <staff>".
 *   - Staff:  plain bold name, job-title label, building icon + work location, group icon +
 *             "Assigned: N Clients (…)", and a "View Staff →" footer link.
 * `assignedHtml` is a pre-built, already-escaped HTML fragment (so the count can be bold).
 */
const infoHtml = ({ type, color, name, avatarUrl, initials, status, tags, typeLabel, locationText, assignedLabel, assignedHtml, viewLabel, nameIsLink }) => {
    const isClient = type === MAP_MARKER_TYPES.CLIENT;
    const avatar = avatarUrl
        ? `<img src="${escapeHtml(avatarUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />`
        : escapeHtml(initials);
    // Reuse the app's StatusCard so map pills match the rest of the product; the InfoWindow takes
    // an HTML string, so render the (purely presentational) component to static markup.
    const statusPill = status ? renderToStaticMarkup(<StatusCard status={status} />) : '';
    const locationIcon = isClient ? PIN_ICON : BUILDING_ICON;
    const assignedIcon = isClient ? PROVIDER_ICON : GROUP_ICON;
    // Client name links to the profile only when the viewer can read clients; otherwise (and for
    // staff, whose name is always plain — nav is via the "View Staff" link) render plain text.
    const nameEl =
        isClient && nameIsLink
            ? `<a class="csm-info__name" href="#">${escapeHtml(name)}</a>`
            : `<span class="csm-info__name csm-info__name--plain">${escapeHtml(name)}</span>`;
    const assignedRow = assignedHtml
        ? `<div style="display:flex;gap:8px;align-items:center;margin-top:10px;font-size:13px;color:#172B4D"><div style="min-width:18px">${assignedIcon}</div><span>${
              assignedLabel ? `${escapeHtml(assignedLabel)}: ` : ''
          }${assignedHtml}</span></div>`
        : '';
    const viewRow = viewLabel ? `<a class="csm-info__viewlink" href="#">${escapeHtml(viewLabel)} &#8594;</a>` : '';
    return (
        `<div class="csm-info">` +
        `<div class="csm-info__top">` +
        `<div class="csm-info__avatar" style="border-color:${color};${avatarUrl ? '' : `background:${color}`}">${avatar}</div>` +
        `<div class="csm-info__main">` +
        `<div class="csm-info__namerow">` +
        `<div class="csm-info__namewrap">` +
        nameEl +
        `<span class="csm-info__typelabel">${escapeHtml(typeLabel)}</span>` +
        `</div>` +
        statusPill +
        `</div>` +
        tagsHtml(tags) +
        `</div></div>` +
        `<div class="csm-info__divider"></div>` +
        `<div style="display:flex;gap:8px;align-items:flex-start;font-size:13px;color:#172B4D"><div style="min-width:18px">${locationIcon}</div><span>${escapeHtml(
            locationText || 'No address on file'
        )}</span></div>` +
        assignedRow +
        viewRow +
        `</div>`
    );
};

// White glyphs shown inside the legend's colored circle badges (blue = client, purple = staff).
const ClientLegendGlyph = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
            d="M10.5624 16.5013C10.4596 16.5012 10.3583 16.4758 10.2677 16.4273C10.177 16.3788 10.0997 16.3086 10.0427 16.223L8.97835 14.6262H7.74979C7.58399 14.6262 7.42498 14.5603 7.30774 14.4431C7.1905 14.3258 7.12463 14.1668 7.12463 14.001C7.12463 13.8352 7.1905 13.6762 7.30774 13.559C7.42498 13.4417 7.58399 13.3759 7.74979 13.3759H9.31212C9.41495 13.3759 9.51618 13.4014 9.60684 13.4499C9.6975 13.4984 9.7748 13.5686 9.83189 13.6541L10.4844 14.6329L11.878 11.8458C11.9281 11.7457 12.0042 11.6609 12.0982 11.6002C12.1923 11.5395 12.3009 11.5052 12.4128 11.5008C12.5246 11.4952 12.6358 11.5202 12.7345 11.573C12.8333 11.6259 12.9157 11.7046 12.973 11.8008L14.6659 14.6232H15.8749C16.0407 14.6232 16.1997 14.689 16.317 14.8063C16.4342 14.9235 16.5001 15.0825 16.5001 15.2483C16.5001 15.4141 16.4342 15.5731 16.317 15.6904C16.1997 15.8076 16.0407 15.8735 15.8749 15.8735H14.3126C14.2051 15.8739 14.0992 15.8464 14.0054 15.7939C13.9116 15.7413 13.8329 15.6654 13.7771 15.5735L12.4878 13.4239L11.122 16.1555C11.0729 16.2534 10.999 16.3368 10.9077 16.3972C10.8163 16.4576 10.7107 16.493 10.6014 16.4998L10.5624 16.5013Z"
            fill="white"
        />
        <path
            d="M7.12525 7.7508C8.85136 7.7508 10.2507 6.35151 10.2507 4.6254C10.2507 2.89929 8.85136 1.5 7.12525 1.5C5.39914 1.5 3.99985 2.89929 3.99985 4.6254C3.99985 6.35151 5.39914 7.7508 7.12525 7.7508Z"
            fill="white"
        />
        <path
            d="M5.87496 14.0005C5.87496 13.5032 6.07252 13.0263 6.42417 12.6746C6.77581 12.323 7.25275 12.1254 7.75006 12.1254H9.31238C9.63123 12.1251 9.94487 12.2062 10.2237 12.3609L10.7607 11.2861C10.8955 11.022 11.0914 10.7939 11.3321 10.6207C11.5728 10.4474 11.8513 10.3341 12.1445 10.2901C11.8563 9.89135 11.4777 9.56658 11.0398 9.34235C10.6019 9.11811 10.1171 9.00079 9.62515 9H4.6254C3.79649 9 3.00154 9.32928 2.41541 9.91541C1.82928 10.5015 1.5 11.2965 1.5 12.1254L1.5 14.1565C1.5 14.2808 1.54939 14.4001 1.6373 14.488C1.72521 14.5759 1.84445 14.6253 1.96877 14.6253H5.99047C5.9163 14.4252 5.87723 14.2138 5.87496 14.0005Z"
            fill="white"
        />
    </svg>
);

const StaffLegendGlyph = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
            d="M14.3027 9.57428H12.8322C12.6371 9.57423 12.4428 9.60025 12.2546 9.65167C12.0709 9.28961 11.7906 8.98545 11.4447 8.77288C11.0989 8.5603 10.7009 8.4476 10.2949 8.44727H7.70232C7.29634 8.4476 6.89838 8.5603 6.55251 8.77288C6.20663 8.98545 5.92634 9.28961 5.74268 9.65167C5.55443 9.60025 5.36015 9.57423 5.16501 9.57428H3.69735C3.1148 9.57501 2.55632 9.80675 2.1444 10.2187C1.73247 10.6306 1.50073 11.1891 1.5 11.7716L1.5 14.121C1.50018 14.4707 1.63912 14.806 1.88631 15.0533C2.1335 15.3006 2.46874 15.4397 2.81841 15.4401H15.1816C15.5311 15.4397 15.8663 15.3007 16.1134 15.0535C16.3606 14.8064 16.4996 14.4712 16.5 14.1217V11.7723C16.4995 11.1896 16.2678 10.631 15.8558 10.2189C15.4439 9.80684 14.8853 9.57501 14.3027 9.57428V9.57428ZM5.50774 10.6446V14.5584H2.81841C2.70191 14.5582 2.59023 14.5118 2.50786 14.4295C2.42548 14.3471 2.37912 14.2354 2.37894 14.1189V11.7696C2.3793 11.42 2.51833 11.0849 2.7655 10.8377C3.01267 10.5905 3.3478 10.4515 3.69735 10.4511H5.16224C5.27896 10.4511 5.39514 10.4667 5.50774 10.4974C5.50774 10.5472 5.50774 10.5956 5.50774 10.6446ZM11.6189 14.5584H6.38391V10.6446C6.38428 10.2951 6.5233 9.95994 6.77047 9.71277C7.01764 9.4656 7.35277 9.32657 7.70232 9.32621H10.2977C10.6472 9.32657 10.9824 9.4656 11.2295 9.71277C11.4767 9.95994 11.6157 10.2951 11.6161 10.6446L11.6189 14.5584ZM15.6266 14.1189C15.6264 14.2354 15.58 14.3471 15.4977 14.4295C15.4153 14.5118 15.3036 14.5582 15.1871 14.5584H12.495V10.6446C12.495 10.5956 12.495 10.5472 12.4895 10.4995C12.6021 10.4688 12.7183 10.4532 12.835 10.4532H14.3033C14.6529 10.4536 14.988 10.5926 15.2352 10.8398C15.4824 11.0869 15.6214 11.4221 15.6218 11.7716L15.6266 14.1189Z"
            fill="white"
        />
        <path
            d="M4.43186 5.42188C4.04565 5.42188 3.66811 5.5364 3.34698 5.75097C3.02586 5.96554 2.77557 6.27052 2.62777 6.62733C2.47997 6.98415 2.4413 7.37677 2.51665 7.75557C2.59199 8.13436 2.77797 8.48231 3.05107 8.7554C3.32416 9.0285 3.67211 9.21448 4.0509 9.28983C4.4297 9.36518 4.82233 9.32651 5.17914 9.17871C5.53596 9.03091 5.84094 8.78062 6.0555 8.45949C6.27007 8.13837 6.3846 7.76083 6.3846 7.37461C6.38405 6.85688 6.17814 6.36051 5.81205 5.99442C5.44596 5.62833 4.94959 5.42242 4.43186 5.42188V5.42188ZM4.43186 8.44772C4.21949 8.44772 4.01188 8.38475 3.83529 8.26676C3.65871 8.14877 3.52108 7.98106 3.4398 7.78485C3.35853 7.58864 3.33727 7.37273 3.3787 7.16444C3.42013 6.95614 3.5224 6.7648 3.67257 6.61463C3.82275 6.46445 4.01408 6.36219 4.22238 6.32075C4.43067 6.27932 4.64658 6.30059 4.84279 6.38186C5.039 6.46313 5.2067 6.60076 5.32469 6.77735C5.44268 6.95393 5.50566 7.16154 5.50566 7.37392C5.50593 7.51514 5.47837 7.65503 5.42453 7.78559C5.3707 7.91614 5.29166 8.03481 5.19193 8.13479C5.0922 8.23478 4.97374 8.31412 4.84332 8.36829C4.7129 8.42246 4.57309 8.45039 4.43186 8.45048V8.44772Z"
            fill="white"
        />
        <path
            d="M8.99954 3C8.48363 3 7.97931 3.15299 7.55035 3.43961C7.12138 3.72623 6.78705 4.13362 6.58962 4.61026C6.39219 5.0869 6.34052 5.61138 6.44117 6.11738C6.54182 6.62337 6.79026 7.08816 7.15506 7.45296C7.51987 7.81777 7.98465 8.0662 8.49065 8.16685C8.99665 8.2675 9.52112 8.21584 9.99776 8.01841C10.4744 7.82098 10.8818 7.48664 11.1684 7.05768C11.455 6.62872 11.608 6.1244 11.608 5.60849C11.6073 4.9169 11.3322 4.25384 10.8432 3.76482C10.3542 3.27579 9.69113 3.00073 8.99954 3V3ZM8.99954 7.33803C8.65788 7.33803 8.32388 7.23672 8.0398 7.0469C7.75572 6.85708 7.53432 6.58729 7.40357 6.27163C7.27282 5.95598 7.2386 5.60864 7.30526 5.27355C7.37191 4.93845 7.53644 4.63064 7.77804 4.38905C8.01963 4.14746 8.32743 3.98293 8.66252 3.91628C8.99762 3.84962 9.34496 3.88383 9.66062 4.01458C9.97627 4.14533 10.2461 4.36675 10.4359 4.65083C10.6257 4.93491 10.727 5.2689 10.727 5.60849C10.7259 6.06838 10.5436 6.50713 10.2198 6.83086C9.89612 7.15459 9.45736 7.33694 8.99954 7.33803Z"
            fill="white"
        />
        <path
            d="M13.5693 5.42188C13.1831 5.42188 12.8056 5.5364 12.4844 5.75097C12.1633 5.96554 11.913 6.27052 11.7652 6.62733C11.6174 6.98415 11.5788 7.37677 11.6541 7.75557C11.7294 8.13436 11.9154 8.48231 12.1885 8.7554C12.4616 9.0285 12.8096 9.21448 13.1883 9.28983C13.5671 9.36518 13.9598 9.32651 14.3166 9.17871C14.6734 9.03091 14.9784 8.78062 15.193 8.45949C15.4075 8.13837 15.5221 7.76083 15.5221 7.37461C15.5215 6.85688 15.3156 6.36051 14.9495 5.99442C14.5834 5.62833 14.087 5.42242 13.5693 5.42188V5.42188ZM13.5693 8.44772C13.3569 8.44772 13.1493 8.38475 12.9728 8.26676C12.7962 8.14877 12.6585 7.98106 12.5773 7.78485C12.496 7.58864 12.4747 7.37273 12.5161 7.16444C12.5576 6.95614 12.6598 6.7648 12.81 6.61463C12.9602 6.46445 13.1515 6.36219 13.3598 6.32075C13.5681 6.27932 13.784 6.30059 13.9802 6.38186C14.1765 6.46313 14.3442 6.60076 14.4621 6.77735C14.5801 6.95393 14.6431 7.16154 14.6431 7.37392C14.6434 7.51514 14.6158 7.65503 14.562 7.78559C14.5081 7.91614 14.4291 8.03481 14.3294 8.13479C14.2297 8.23478 14.1112 8.31412 13.9808 8.36829C13.8503 8.42246 13.7105 8.45039 13.5693 8.45048V8.44772Z"
            fill="white"
        />
    </svg>
);

export const ClientsStaffMap = ({ handleClose }) => {
    const dispatch = useDispatch();
    const { mapLocations } = useSelector((state) => state.map);
    const loading = !!FindLoad(GET_MAP_LOCATIONS_ACTION)?.length;
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const overlaysRef = useRef([]);
    const connectorsRef = useRef([]);
    const infoRef = useRef(null);
    const spiderRef = useRef({ active: null, legs: [], markers: [] });
    const activeNavRef = useRef(null);
    const selectedStaffRef = useRef(null);
    const clearSelectionRef = useRef(null);
    const hoverDistanceRef = useRef(null);
    const lineTooltipRef = useRef(null);
    const { open, close } = useModal();
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [googleReady, setGoogleReady] = useState(!!window.google?.maps);
    const [visible, setVisible] = useState({
        [MAP_MARKER_TYPES.CLIENT]: true,
        [MAP_MARKER_TYPES.STAFF]: true,
    });
    // Filter state lives here, not in `location.state`: the map is a drawer over whatever page is
    // open, and must not rewrite that page's own filters. Keys: MAP_FILTER_KEYS.
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const clients = mapLocations?.clients || [];
    const staff = mapLocations?.staff || [];
    const canReadClient = RolePermission([PermissionList.CLIENT_READ?.code]);
    const canReadStaff = RolePermission([PermissionList.STAFF_READ?.code]);

    // Statuses are filtered server-side (the API plots only active people by default); keyed on the
    // id list so picking the same set again doesn't refetch.
    const staffStatusIds = idsOf(filters[MAP_FILTER_KEYS.STAFF_STATUSES]);
    const clientStatusIds = idsOf(filters[MAP_FILTER_KEYS.CLIENT_STATUSES]);
    const statusKey = `${staffStatusIds.join()}|${clientStatusIds.join()}`;

    useEffect(() => {
        dispatch(
            mapActions.getMapLocations({
                ...(staffStatusIds.length ? { staffStatuses: staffStatusIds } : {}),
                ...(clientStatusIds.length ? { clientStatuses: clientStatusIds } : {}),
            })
        );
    }, [dispatch, statusKey]);

    // People + place filters are applied client-side to the loaded pins.
    const states = idsOf(filters[MAP_FILTER_KEYS.STATES]);
    const cities = idsOf(filters[MAP_FILTER_KEYS.CITIES]);
    const filteredStaff = useMemo(
        () => applyFilters(staff, idsOf(filters[MAP_FILTER_KEYS.STAFFS]), states, cities),
        [staff, filters]
    );
    const filteredClients = useMemo(
        () => applyFilters(clients, idsOf(filters[MAP_FILTER_KEYS.CLIENTS]), states, cities),
        [clients, filters]
    );

    // State/City options come from the loaded pins; City narrows to the picked states.
    const allPins = useMemo(() => [...clients, ...staff], [clients, staff]);
    const stateOptions = useMemo(() => placeOptions(allPins, 'state'), [allPins]);
    const cityOptions = useMemo(() => placeOptions(pinsInStates(allPins, states), 'city'), [allPins, filters]);

    /**
     * Every write to the filter state lands here, from the popover and from the chip bar alike, so a
     * State change (or a State chip removal) always drops the cities it no longer offers.
     */
    const saveFilters = (next) => setFilters(pruneCities(next, allPins));

    /** One key set, or dropped when emptied — an absent key is "All" (what the chips and count read). */
    const setFilter = (key, value, extra = {}) =>
        setFilters((prev) => {
            const next = { ...prev, ...extra, [key]: value };
            [key, ...Object.keys(extra)].forEach((k) => {
                if (!Array.isArray(next[k]) || !next[k].length) delete next[k];
            });
            return pruneCities(next, allPins);
        });

    const handlePersonSelect = (e) => {
        const { name, value, full } = e?.target || {};
        const fullKey = name === MAP_FILTER_KEYS.STAFFS ? MAP_FILTER_KEYS.STAFF_FULL : MAP_FILTER_KEYS.CLIENT_FULL;
        setFilter(name, value || [], { [fullKey]: full || [] });
    };

    const activeFiltersCount = [
        MAP_FILTER_KEYS.STAFFS,
        MAP_FILTER_KEYS.STAFF_STATUSES,
        MAP_FILTER_KEYS.CLIENTS,
        MAP_FILTER_KEYS.CLIENT_STATUSES,
        MAP_FILTER_KEYS.STATES,
        MAP_FILTER_KEYS.CITIES,
    ].filter((k) => filters[k]?.length).length;

    const handleResetAll = (handleCloseFilters) => {
        setFilters({});
        handleCloseFilters?.();
    };

    // The same filters, declared for the chip bar (removal and "Clear Filters" derive from it).
    const chipFilters = [
        { name: 'Staff', key: MAP_FILTER_KEYS.STAFF_FULL, pairKey: MAP_FILTER_KEYS.STAFFS, render: (i) => renderStaffName(i) },
        { name: 'Staff Status', key: MAP_FILTER_KEYS.STAFF_STATUSES, options: staffStatuses },
        { name: 'Client', key: MAP_FILTER_KEYS.CLIENT_FULL, pairKey: MAP_FILTER_KEYS.CLIENTS, render: (i) => renderClientName(i) },
        { name: 'Client Status', key: MAP_FILTER_KEYS.CLIENT_STATUSES, options: clientStatuses },
        { name: 'State', key: MAP_FILTER_KEYS.STATES, render: (i) => i?.name },
        { name: 'City', key: MAP_FILTER_KEYS.CITIES, render: (i) => i?.name },
    ];

    useEffect(() => {
        if (googleReady) return undefined;
        const interval = setInterval(() => {
            if (window.google?.maps) {
                setGoogleReady(true);
                clearInterval(interval);
            }
        }, 200);
        return () => clearInterval(interval);
    }, [googleReady]);

    useEffect(() => {
        if (!googleReady || !containerRef.current || mapRef.current) return;
        const g = window.google.maps;
        mapRef.current = new g.Map(containerRef.current, {
            center: DEFAULT_MAP_CENTER,
            zoom: DEFAULT_MAP_ZOOM,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
            // 'greedy' = plain scroll always zooms (no ⌘/Ctrl needed) and one-finger drag pans,
            // so the "Use ⌘ + scroll to zoom" overlay never appears.
            gestureHandling: 'greedy',
            clickableIcons: false,
            styles: MAP_STYLES,
        });
        infoRef.current = new g.InfoWindow({ pixelOffset: new g.Size(0, -64) });
        g.event.addListener(infoRef.current, 'domready', () => {
            const go = (e) => {
                e.preventDefault();
                if (activeNavRef.current) {
                    window.open(activeNavRef.current, '_blank', 'noopener');
                }
            };
            const nameLink = document.querySelector('a.csm-info__name');
            if (nameLink) nameLink.onclick = go;
            const viewLink = document.querySelector('.csm-info__viewlink');
            if (viewLink) viewLink.onclick = go;
        });
    }, [googleReady]);

    // (Re)draw markers whenever data, layer toggles or filters change.
    useEffect(() => {
        if (!googleReady || !mapRef.current) return undefined;
        const g = window.google.maps;

        // HTML avatar-pin overlay (classic markers can't render a photo).
        class HtmlMarker extends g.OverlayView {
            constructor(position, el, onClick) {
                super();
                this._position = position;
                this._el = el;
                this._onClick = onClick;
            }
            onAdd() {
                // Stop the pin/cluster click from bubbling to the map, otherwise the map's own
                // 'click' (which closes the info card) fires right after open and the card never
                // stays up. mousedown is stopped too so the click isn't swallowed by a map drag.
                this._clickHandler = (e) => {
                    e.stopPropagation();
                    this._onClick(e);
                };
                this._downHandler = (e) => e.stopPropagation();
                this._el.addEventListener('click', this._clickHandler);
                this._el.addEventListener('mousedown', this._downHandler);
                this.getPanes().overlayMouseTarget.appendChild(this._el);
            }
            draw() {
                const point = this.getProjection().fromLatLngToDivPixel(new g.LatLng(this._position));
                if (point) {
                    this._el.style.left = `${point.x}px`;
                    this._el.style.top = `${point.y}px`;
                }
            }
            onRemove() {
                this._el.removeEventListener('click', this._clickHandler);
                this._el.removeEventListener('mousedown', this._downHandler);
                this._el.remove();
            }
        }

        // Non-interactive HTML overlay for the role chip sitting at a connector line's midpoint.
        class HtmlLabel extends g.OverlayView {
            constructor(position, el) {
                super();
                this._position = position;
                this._el = el;
            }
            onAdd() {
                this.getPanes().floatPane.appendChild(this._el);
            }
            draw() {
                const point = this.getProjection().fromLatLngToDivPixel(new g.LatLng(this._position));
                if (point) {
                    this._el.style.left = `${point.x}px`;
                    this._el.style.top = `${point.y}px`;
                }
            }
            onRemove() {
                this._el.remove();
            }
        }

        const clearOverlays = () => {
            overlaysRef.current.forEach((o) => o.setMap(null));
            overlaysRef.current = [];
        };

        // Layer visibility (legend) on top of the filters (already applied to filteredStaff/Clients).
        const staffToShow = visible[MAP_MARKER_TYPES.STAFF] ? filteredStaff : [];
        const clientsToShow = visible[MAP_MARKER_TYPES.CLIENT] ? filteredClients : [];

        // One flat list (clients first so staff pins draw on top) — the unit that gets clustered.
        // Records without a real coordinate (empty/NaN/out-of-range) are dropped: they'd otherwise
        // corrupt the auto-fit bounds and drop a broken pin at an impossible spot. Then markers that
        // share an exact coordinate are spread onto a tiny ring so zoom can pull them apart like any
        // other cluster (see spreadCoincidentItems).
        const items = spreadCoincidentItems([
            ...clientsToShow.filter(isPlottableCoord).map((c) => ({ item: c, type: MAP_MARKER_TYPES.CLIENT })),
            ...staffToShow.filter(isPlottableCoord).map((s) => ({ item: s, type: MAP_MARKER_TYPES.STAFF })),
        ]);

        // Position lookup for currently-plotted clients — connectors only link to clients that are
        // actually on the map right now, so they automatically respect the layer toggle + filters.
        // Built from the spread positions so a connector points at the pin the user actually sees.
        const clientPositionById = new Map(
            items
                .filter((e) => e.type === MAP_MARKER_TYPES.CLIENT)
                .map((e) => [e.item.id, { lat: Number(e.item.lat), lng: Number(e.item.lng) }])
        );

        // Real coordinates by id — distances are measured between the people's own points, never the
        // nudged (coincident spread) or fanned-out (spiderfied) positions their pins are drawn at.
        const realPos = new Map(
            [...filteredClients, ...filteredStaff].map((p) => [p.id, { lat: Number(p.lat), lng: Number(p.lng) }])
        );

        // Pin elements by id/type — lets a staff selection dim the pins that aren't involved and
        // enlarge the selected staff.
        const pins = [];
        // "+N" bubbles with the ids they swallowed — a cluster fades back only when none of the
        // people inside it are part of the current selection.
        const clusterPins = [];
        // Ids involved in the active focus (selected staff + its plotted clients), or null when no
        // focus is on. Kept so markers created *after* focus was applied (spiderfied pins) match.
        let focusedIds = null;

        // Mileage tooltip that follows the cursor while it is over a connector line — one element,
        // reused for every line, living in the map's own div so it is positioned in map pixels.
        const hideLineTooltip = () => {
            lineTooltipRef.current?.remove();
            lineTooltipRef.current = null;
        };
        const showLineTooltip = (label, domEvent) => {
            const mapDiv = mapRef.current?.getDiv();
            if (!label || !mapDiv || !domEvent) return;
            if (!lineTooltipRef.current) {
                const tip = document.createElement('span');
                tip.className = 'csm-connector-label__chip csm-connector-label__chip--distance csm-line-tooltip';
                mapDiv.appendChild(tip);
                lineTooltipRef.current = tip;
            }
            const tip = lineTooltipRef.current;
            const rect = mapDiv.getBoundingClientRect();
            tip.textContent = label.text;
            tip.style.left = `${domEvent.clientX - rect.left}px`;
            tip.style.top = `${domEvent.clientY - rect.top}px`;
        };

        const clearConnectors = () => {
            hideLineTooltip();
            connectorsRef.current.forEach((o) => o.setMap(null));
            connectorsRef.current = [];
        };

        // Focus mode: fade every pin that isn't the selected staff or one of its clients, and bump up
        // the selected staff. Called only when the staff actually has drawn connectors. "+N" bubbles
        // fade with the same rule, but only when *nobody* inside them is involved — a cluster holding
        // one of the staff's clients is a connector endpoint and has to stay readable.
        const applyStaffFocus = (staffItem) => {
            const involved = new Set([staffItem.id]);
            (Array.isArray(staffItem.clients) ? staffItem.clients : []).forEach((link) => {
                if (clientPositionById.has(link.id)) involved.add(link.id);
            });
            focusedIds = involved;
            pins.forEach((p) => {
                p.el.classList.toggle('csm-pin--dimmed', !involved.has(p.id));
                p.el.classList.toggle('csm-pin--selected', p.type === MAP_MARKER_TYPES.STAFF && p.id === staffItem.id);
            });
            clusterPins.forEach((c) => {
                c.el.classList.toggle('csm-cluster--dimmed', !c.ids.some((id) => involved.has(id)));
            });
        };
        // The hover-distance chip on a client pin (see addMarker) — at most one at a time.
        const removeHoverDistance = () => {
            hoverDistanceRef.current?.remove();
            hoverDistanceRef.current = null;
        };
        const clearFocus = () => {
            removeHoverDistance();
            focusedIds = null;
            pins.forEach((p) => {
                p.el.classList.remove('csm-pin--dimmed', 'csm-pin--selected');
            });
            clusterPins.forEach((c) => {
                c.el.classList.remove('csm-cluster--dimmed');
            });
        };

        // Drop the staff selection entirely — overlays, focus, InfoWindow card and the React bar.
        // Exposed via a ref so the selection bar's "Clear Selection"/close can call it.
        clearSelectionRef.current = () => {
            selectedStaffRef.current = null;
            clearConnectors();
            clearFocus();
            if (infoRef.current) infoRef.current.close();
            setSelectedStaff(null);
        };

        // Draw a dashed line + role chip from the clicked staff to each of their plotted clients.
        // A staff can serve many clients (`staff.clients: [{ id, role }]`); each drawn as one link.
        // Returns how many links were actually drawn (clients that are on the map right now).
        const drawConnectors = (staffItem, staffPos) => {
            clearConnectors();
            const links = Array.isArray(staffItem.clients) ? staffItem.clients : [];
            const origin = realPos.get(staffItem.id);
            const distanceChips = [];
            let drawn = 0;
            links.forEach((link) => {
                const clientPos = clientPositionById.get(link.id);
                if (!clientPos) return; // client is filtered out, layer-hidden, or has no mappable address
                const line = new g.Polyline({
                    path: [staffPos, clientPos],
                    strokeOpacity: 0,
                    // strokeOpacity:0 + repeating dot icon = a dashed line.
                    icons: [
                        {
                            icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: MAP_CONNECTOR_COLOR, scale: 3 },
                            offset: '0',
                            repeat: '12px',
                        },
                    ],
                    // Invisible stroke (opacity 0) — the weight only widens the hover hit area.
                    strokeWeight: MAP_CONNECTOR_HIT_WEIGHT,
                    zIndex: 1,
                });
                line.setMap(mapRef.current);
                connectorsRef.current.push(line);
                drawn += 1;

                // Driving miles when cached, else straight-line ("≈") until the lookup below lands.
                const dest = realPos.get(link.id);
                const label = distanceLabel(bestDistance(origin, dest, cachedDrivingMiles(origin, dest)));

                // Hovering the line shows its mileage at the cursor. `hover.label` is upgraded in
                // place when the driving lookup lands, so the tooltip and the midpoint chip agree.
                const hover = { label };
                if (label) {
                    line.addListener(MAP_POLY_EVENTS.OVER, (e) => showLineTooltip(hover.label, e.domEvent));
                    line.addListener(MAP_POLY_EVENTS.MOVE, (e) => showLineTooltip(hover.label, e.domEvent));
                    line.addListener(MAP_POLY_EVENTS.OUT, hideLineTooltip);
                }
                const chips = [
                    label && {
                        text: label.text,
                        title: label.title,
                        dest,
                        hover,
                        className: 'csm-connector-label__chip csm-connector-label__chip--distance',
                    },
                    link.role && { text: link.role, className: 'csm-connector-label__chip' },
                ].filter(Boolean);
                if (chips.length) {
                    const mid = { lat: (staffPos.lat + clientPos.lat) / 2, lng: (staffPos.lng + clientPos.lng) / 2 };
                    const wrap = document.createElement('div');
                    wrap.className = 'csm-connector-label';
                    chips.forEach(({ text, title, dest: chipDest, hover: chipHover, className }) => {
                        const chip = document.createElement('span');
                        chip.className = className;
                        chip.textContent = text;
                        if (title) chip.title = title;
                        if (chipDest) distanceChips.push({ chip, dest: chipDest, hover: chipHover });
                        wrap.appendChild(chip);
                    });
                    const overlay = new HtmlLabel(mid, wrap);
                    overlay.setMap(mapRef.current);
                    connectorsRef.current.push(overlay);
                }
            });
            // One batched driving lookup for all of this staff's links; chips that were removed in
            // the meantime (selection cleared, re-cluster) are skipped.
            if (origin && distanceChips.length) {
                drivingMiles(
                    origin,
                    distanceChips.map((c) => c.dest)
                ).then((results) =>
                    distanceChips.forEach(({ chip, dest, hover }, i) => {
                        const next = chip.isConnected && distanceLabel(bestDistance(origin, dest, results[i]));
                        if (next) {
                            chip.textContent = next.text;
                            chip.title = next.title;
                            hover.label = next;
                        }
                    })
                );
            }
            return drawn;
        };

        const addMarker = (item, type, positionOverride) => {
            // Spiderfied pins pass an explicit position (fanned out around the shared point); everyone
            // else sits at their own coordinate.
            const position = positionOverride || { lat: Number(item.lat), lng: Number(item.lng) };
            const color = MAP_COLORS[type];
            const isClient = type === MAP_MARKER_TYPES.CLIENT;
            const initials = initialsOf(item.firstName, item.lastName, isClient ? item.code : '');
            // Card shows the plain name (no "(CODE)" prefix that renderClientName adds); fall back to code/type.
            const plainName = [item.firstName, item.middleName ? `${item.middleName[0]}.` : '', item.lastName]
                .filter(Boolean)
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();
            const cardName = plainName || (isClient ? item.code || 'Client' : 'Staff');
            const el = createPinElement({ color, avatarUrl: item.avatarUrl, initials });
            const nav = isClient ? `/client/${item.id}` : `/staff/${item.id}`;
            // Client card → person icon + "Assigned to: <staff>".
            // Staff card → group icon + "Assigned: <b>N Clients</b> (a, b, +X more)" (count emphasized).
            const staffClientsHtml = () => {
                const list = Array.isArray(item.clients) ? item.clients : [];
                if (!list.length) return null;
                const names = list.map((c) => c.name).filter(Boolean);
                const shown = names.slice(0, 2).map(escapeHtml);
                const extra = names.length - shown.length;
                const suffix = shown.length ? ` (${shown.join(', ')}${extra > 0 ? `, +${extra} more` : ''})` : '';
                return `<b>${list.length} Client${list.length === 1 ? '' : 's'}</b>${suffix}`;
            };
            const content = infoHtml({
                type,
                color,
                name: cardName,
                avatarUrl: item.avatarUrl,
                initials,
                status: item.status,
                tags: item.tags,
                typeLabel: isClient ? 'Client' : item.title || 'Staff',
                locationText: isClient ? item.formattedAddress : item.workLocation || item.formattedAddress,
                assignedLabel: isClient ? 'Assigned to' : 'Assigned',
                assignedHtml: isClient ? (item.assignedTo ? escapeHtml(item.assignedTo) : null) : staffClientsHtml(),
                // "View Staff" link only when the viewer can read staff; client name links only with CLIENT_READ.
                viewLabel: isClient ? null : canReadStaff ? 'View Staff' : null,
                nameIsLink: canReadClient,
            });
            const marker = new HtmlMarker(position, el, () => {
                activeNavRef.current = nav;
                infoRef.current.setContent(content);
                infoRef.current.setPosition(position);
                // Clicking a pin may pan the card into view; zoom-driven restores must not (see reapplySelection).
                infoRef.current.setOptions({ disableAutoPan: false });
                infoRef.current.open({ map: mapRef.current });
                // Staff → draw connectors + focus (dim others, enlarge selected) + selection bar;
                // client → reset selection.
                if (isClient) {
                    selectedStaffRef.current = null;
                    clearConnectors();
                    clearFocus();
                    setSelectedStaff(null);
                } else {
                    // Remember the selection so a zoom re-cluster can restore it (see reapplySelection).
                    selectedStaffRef.current = { item, position, content, nav };
                    const drawn = drawConnectors(item, position);
                    if (drawn > 0) applyStaffFocus(item);
                    else clearFocus();
                    // Drive the React selection bar + Assigned Clients modal.
                    setSelectedStaff(item);
                }
            });
            // While a staff is selected, hovering ANY client pin shows the miles from that staff. The
            // chip lives inside the pin element, so a re-cluster (which rebuilds pins) drops it too.
            if (isClient) {
                el.addEventListener('mouseenter', () => {
                    const sel = selectedStaffRef.current;
                    // Both ends use the people's own coordinates, never a spiderfied pin's fanned-out spot.
                    const origin = sel && realPos.get(sel.item.id);
                    const dest = realPos.get(item.id);
                    const label = origin && dest && distanceLabel(bestDistance(origin, dest, cachedDrivingMiles(origin, dest)));
                    if (!label) return;
                    removeHoverDistance();
                    const chip = document.createElement('span');
                    chip.className = 'csm-connector-label__chip csm-connector-label__chip--distance csm-pin__distance';
                    chip.textContent = label.text;
                    chip.title = label.title;
                    el.appendChild(chip);
                    hoverDistanceRef.current = chip;
                    // Upgrade to driving miles once known (cached after the first lookup per pair).
                    drivingMiles(origin, [dest]).then(([driving]) => {
                        const next = chip.isConnected && distanceLabel(bestDistance(origin, dest, driving));
                        if (next) {
                            chip.textContent = next.text;
                            chip.title = next.title;
                        }
                    });
                });
                el.addEventListener('mouseleave', removeHoverDistance);
            }
            marker.setMap(mapRef.current);
            overlaysRef.current.push(marker);
            pins.push({ el, id: item.id, type });
            // Pins born while a focus is active (spiderfied out of a group) join it right away,
            // otherwise expanding a group would punch a bright hole in the faded-back map.
            if (focusedIds && !focusedIds.has(item.id)) el.classList.add('csm-pin--dimmed');
            return marker;
        };

        // Fold an expanded group back: drop the leader lines and the fanned-out pins, leaving the
        // "+N" bubble in place. (A full re-render/zoom disposes the pins anyway via clearOverlays,
        // but a manual second-click collapse has to remove them itself.)
        const collapseSpider = () => {
            spiderRef.current.legs.forEach((l) => l.setMap(null));
            spiderRef.current.markers.forEach((m) => m.setMap(null));
            const gone = new Set(spiderRef.current.markers);
            overlaysRef.current = overlaysRef.current.filter((o) => !gone.has(o));
            spiderRef.current = { active: null, legs: [], markers: [] };
        };

        // Fan a group of co-located markers out into a ring around their shared point so each one is
        // individually visible and clickable. Zoom can't separate identical coordinates, so this is
        // the only way to reach people who share an exact location (same household/office address).
        const spiderfyCluster = (cluster) => {
            const map = mapRef.current;
            const proj = map.getProjection();
            const zoom = map.getZoom();
            if (!proj || typeof zoom !== 'number') return;
            collapseSpider();
            const scale = 2 ** zoom;
            const centerWorld = proj.fromLatLngToPoint(new g.LatLng(cluster.lat, cluster.lng));
            const n = cluster.items.length;
            // Radius grows with the count so pins don't overlap; start above the pin size (~40px).
            const radius = 34 + n * 6;
            cluster.items.forEach((entry, i) => {
                const angle = (2 * Math.PI * i) / n - Math.PI / 2; // first pin straight up
                const dx = Math.cos(angle) * radius;
                const dy = Math.sin(angle) * radius;
                const world = new g.Point(centerWorld.x + dx / scale, centerWorld.y + dy / scale);
                const latLng = proj.fromPointToLatLng(world);
                const spiderPos = { lat: latLng.lat(), lng: latLng.lng() };
                // Leader line from the shared point to the fanned-out pin.
                const leg = new g.Polyline({
                    path: [{ lat: cluster.lat, lng: cluster.lng }, spiderPos],
                    strokeColor: '#98A2B3',
                    strokeOpacity: 0.9,
                    strokeWeight: 1.5,
                    zIndex: 1,
                });
                leg.setMap(map);
                spiderRef.current.legs.push(leg);
                const m = addMarker(entry.item, entry.type, spiderPos);
                spiderRef.current.markers.push(m);
            });
            spiderRef.current.active = cluster;
        };

        // A merged "+N" bubble. Points that share an exact location can never be split by zoom, so
        // clicking those spiders them apart; a spread-out group just zooms to fit their bounds.
        const addClusterMarker = (cluster) => {
            const el = document.createElement('div');
            // Every cluster is single-type now (client groups and staff groups are clustered
            // separately), so color the bubble by that type: blue for clients, purple for staff.
            const clusterType = cluster.items[0].type;
            el.className = `csm-cluster${clusterType === MAP_MARKER_TYPES.STAFF ? ' csm-cluster--staff' : ''}`;
            el.textContent = `+${cluster.items.length}`;
            const first = cluster.items[0].item;
            const allSamePoint = cluster.items.every(
                (entry) => Number(entry.item.lat) === Number(first.lat) && Number(entry.item.lng) === Number(first.lng)
            );
            const marker = new HtmlMarker({ lat: cluster.lat, lng: cluster.lng }, el, () => {
                if (allSamePoint) {
                    // Second click on an already-expanded group folds it back.
                    if (spiderRef.current.active === cluster) collapseSpider();
                    else spiderfyCluster(cluster);
                    return;
                }
                collapseSpider();
                const b = new g.LatLngBounds();
                cluster.items.forEach((entry) => b.extend({ lat: Number(entry.item.lat), lng: Number(entry.item.lng) }));
                mapRef.current.fitBounds(b, 96);
            });
            marker.setMap(mapRef.current);
            overlaysRef.current.push(marker);
            clusterPins.push({ el, ids: cluster.items.map((entry) => entry.item.id) });
        };

        // Greedy pixel-distance clustering: project each point to screen pixels at the current zoom
        // and fold anything within MAP_CLUSTER_RADIUS_PX of an existing cluster's centroid into it.
        // Grouping tightens as you zoom out and loosens as you zoom in — hence the re-run on zoom.
        const computeClusters = (list) => {
            const map = mapRef.current;
            const proj = map.getProjection();
            const zoom = map.getZoom();
            // Projection isn't ready until the first idle — fall back to one marker each.
            if (!proj || typeof zoom !== 'number') {
                return list.map((entry) => ({ items: [entry], lat: Number(entry.item.lat), lng: Number(entry.item.lng) }));
            }
            const scale = 2 ** zoom;
            const toPx = (lat, lng) => {
                const p = proj.fromLatLngToPoint(new g.LatLng(lat, lng));
                return { x: p.x * scale, y: p.y * scale };
            };
            const clusters = [];
            list.forEach((entry) => {
                const lat = Number(entry.item.lat);
                const lng = Number(entry.item.lng);
                const point = toPx(lat, lng);
                let target = null;
                for (const cluster of clusters) {
                    const dx = cluster.px.x - point.x;
                    const dy = cluster.px.y - point.y;
                    if (dx * dx + dy * dy <= MAP_CLUSTER_RADIUS_PX * MAP_CLUSTER_RADIUS_PX) {
                        target = cluster;
                        break;
                    }
                }
                if (target) {
                    target.items.push(entry);
                    target.sumLat += lat;
                    target.sumLng += lng;
                    target.lat = target.sumLat / target.items.length;
                    target.lng = target.sumLng / target.items.length;
                    target.px = toPx(target.lat, target.lng);
                } else {
                    clusters.push({
                        items: [entry],
                        px: point,
                        lat,
                        lng,
                        sumLat: lat,
                        sumLng: lng,
                    });
                }
            });
            return clusters;
        };

        // Re-plot everything for the current zoom: singletons render as pins, groups as "+N" bubbles.
        // Pin elements are recreated and positions may regroup — the open selection is restored
        // afterwards by reapplySelection().
        const renderCluster = () => {
            clearOverlays();
            clearConnectors();
            // A zoom re-cluster rebuilds every overlay, so any open spider must fold back first.
            collapseSpider();
            infoRef.current?.close();
            pins.length = 0;
            clusterPins.length = 0;
            focusedIds = null;
            // Cluster each type on its own so a "+N" bubble is never a mix of clients and staff:
            // co-located people of different types produce two separate, type-colored bubbles.
            const clientEntries = items.filter((e) => e.type === MAP_MARKER_TYPES.CLIENT);
            const staffEntries = items.filter((e) => e.type === MAP_MARKER_TYPES.STAFF);
            [...computeClusters(clientEntries), ...computeClusters(staffEntries)].forEach((cluster) => {
                if (cluster.items.length === 1) {
                    addMarker(cluster.items[0].item, cluster.items[0].type);
                } else {
                    addClusterMarker(cluster);
                }
            });
        };

        // After a re-cluster (e.g. zoom via scroll), restore the selected staff's connectors, focus
        // and info card — but only while that staff is drawn as its own pin. If it has merged into a
        // cluster at this zoom, there's nothing to connect from, so leave it cleared (it comes back
        // when zooming in splits the cluster again).
        const reapplySelection = () => {
            const sel = selectedStaffRef.current;
            if (!sel) return;
            const shown = pins.some((p) => p.type === MAP_MARKER_TYPES.STAFF && p.id === sel.item.id);
            if (!shown) {
                clearConnectors();
                clearFocus();
                infoRef.current?.close();
                return;
            }
            const drawn = drawConnectors(sel.item, sel.position);
            if (drawn > 0) applyStaffFocus(sel.item);
            else clearFocus();
            activeNavRef.current = sel.nav;
            infoRef.current.setContent(sel.content);
            infoRef.current.setPosition(sel.position);
            // Restoring the card after a zoom must NOT recenter the map on the staff — the user is
            // scrolling deliberately. Auto-pan is only wanted on the initial click.
            infoRef.current.setOptions({ disableAutoPan: true });
            infoRef.current.open({ map: mapRef.current });
        };

        renderCluster();
        reapplySelection();

        const bounds = new g.LatLngBounds();
        items.forEach(({ item }) => bounds.extend({ lat: Number(item.lat), lng: Number(item.lng) }));

        // One-time after auto-fit: don't let a lone/tight marker set zoom to street level.
        let capListener;
        if (items.length > 0) {
            mapRef.current.fitBounds(bounds);
            capListener = g.event.addListenerOnce(mapRef.current, 'idle', () => {
                if (mapRef.current.getZoom() > MAX_FIT_ZOOM) mapRef.current.setZoom(MAX_FIT_ZOOM);
            });
        }

        // Re-cluster whenever the zoom level changes (pins merge when zooming out, split zooming in).
        // Only on zoom change — a plain pan needs no re-render since overlays draw off-screen too.
        // Starts null so the first idle always re-clusters once the map projection is ready (the
        // very first renderCluster() above may run before it is, falling back to unclustered pins).
        let lastZoom = null;
        const zoomListener = g.event.addListener(mapRef.current, 'idle', () => {
            const z = mapRef.current.getZoom();
            if (z !== lastZoom) {
                lastZoom = z;
                renderCluster();
                // Redraw the selected staff's connectors/card for the new cluster layout so they
                // survive a scroll-zoom instead of disappearing.
                reapplySelection();
            }
        });

        // Dismissing the card (its close button) drops the selection so it doesn't come back on
        // the next zoom via reapplySelection.
        const closeListener = g.event.addListener(infoRef.current, 'closeclick', () => {
            selectedStaffRef.current = null;
            clearConnectors();
            clearFocus();
            setSelectedStaff(null);
        });

        // Clicking empty map (outside any pin/card) closes the open info card and clears the whole
        // selection — the InfoWindow doesn't dismiss on outside-click on its own. Pin/cluster clicks
        // stop their own propagation (see HtmlMarker.onAdd), so this fires only for real map clicks.
        const mapClickListener = g.event.addListener(mapRef.current, 'click', () => {
            infoRef.current?.close();
            selectedStaffRef.current = null;
            clearConnectors();
            clearFocus();
            collapseSpider();
            setSelectedStaff(null);
        });

        // Runs before the next redraw and on unmount — drop listeners, markers and connector lines
        // (their positions would be stale once data/filters change).
        return () => {
            g.event.removeListener(zoomListener);
            g.event.removeListener(closeListener);
            g.event.removeListener(mapClickListener);
            if (capListener) g.event.removeListener(capListener);
            clearOverlays();
            clearConnectors();
            collapseSpider();
        };
    }, [googleReady, visible, filteredClients, filteredStaff, canReadClient, canReadStaff]);

    const toggle = (type) => setVisible((prev) => ({ ...prev, [type]: !prev[type] }));
    const staffFull = filters[MAP_FILTER_KEYS.STAFF_FULL] || [];
    const clientFull = filters[MAP_FILTER_KEYS.CLIENT_FULL] || [];
    const pickedStaffStatuses = filters[MAP_FILTER_KEYS.STAFF_STATUSES] || [];
    const pickedClientStatuses = filters[MAP_FILTER_KEYS.CLIENT_STATUSES] || [];
    const pickedStates = filters[MAP_FILTER_KEYS.STATES] || [];
    const pickedCities = filters[MAP_FILTER_KEYS.CITIES] || [];
    // Nothing loaded at all vs. filters that match nobody — each gets its own message.
    const nothingLoaded = clients.length + staff.length === 0;
    const showEmpty = !loading && googleReady && filteredClients.length + filteredStaff.length === 0;

    // The selected staff's real point (the selection holds the pin's item, which may be nudged).
    const selectedOrigin = useMemo(() => {
        const real = selectedStaff && staff.find((s) => s.id === selectedStaff.id);
        return real ? { lat: Number(real.lat), lng: Number(real.lng) } : null;
    }, [selectedStaff, staff]);

    // Driving miles to each assigned client, keyed by staff so a switch never shows the last staff's.
    const [driving, setDriving] = useState({ staffId: null, byClient: {} });
    useEffect(() => {
        if (!selectedStaff || !selectedOrigin) return undefined;
        const linked = (selectedStaff.clients || []).map((l) => clients.find((c) => c.id === l.id)).filter(Boolean);
        let cancelled = false;
        drivingMiles(
            selectedOrigin,
            linked.map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }))
        ).then((results) => {
            if (!cancelled) setDriving({ staffId: selectedStaff.id, byClient: Object.fromEntries(linked.map((c, i) => [c.id, results[i]])) });
        });
        return () => {
            cancelled = true;
        };
    }, [selectedStaff, selectedOrigin, clients]);

    // Resolve the selected staff's client links ({ id, role }) against the full client dataset to get
    // each client's name/address/coords, then attach the distance (driving when known, else
    // straight-line) and sort nearest-first.
    const assignedClients = useMemo(() => {
        if (!selectedStaff) return [];
        const links = Array.isArray(selectedStaff.clients) ? selectedStaff.clients : [];
        const drivingFor = driving.staffId === selectedStaff.id ? driving.byClient : {};
        return links
            .map((link) => {
                const full = clients.find((c) => c.id === link.id);
                if (!full) return null;
                const best = bestDistance(selectedOrigin, { lat: Number(full.lat), lng: Number(full.lng) }, drivingFor[full.id]);
                return { ...full, role: link.role, distance: best.miles, distanceKind: best.kind };
            })
            .filter(Boolean)
            .sort((a, b) => {
                if (a.distance == null) return 1;
                if (b.distance == null) return -1;
                return a.distance - b.distance;
            });
    }, [selectedStaff, selectedOrigin, clients, driving]);

    const clearSelection = () => clearSelectionRef.current?.();

    const handleShowAssignments = () => {
        open(<AssignedClientsModal staff={selectedStaff} assignedClients={assignedClients} handleClose={close} />);
    };

    return (
        <div className="clients-staff-map">
            <div className="clients-staff-map__header">
                <p className="clients-staff-map__title">Clients and staff map</p>
                <CloseButton handleCLic={handleClose} />
            </div>

            <div className="clients-staff-map__toolbar">
                {/* The toolbar is a flex column, so without align-self the popover's `position: relative`
                    wrapper stretches full width and the count badge lands at the drawer's right edge. */}
                <FilterPopover activeFiltersCount={activeFiltersCount} onResetAll={handleResetAll} wrapperStyles={{ alignSelf: 'flex-start' }}>
                    <SelectAutocompletePaginationMultiple
                        type={'staff'}
                        name={MAP_FILTER_KEYS.STAFFS}
                        outLabel={'Staff'}
                        uiType={'tableFilter'}
                        defaultStatuses={staffStatusIds.length ? staffStatusIds : DEFAULT_STAFF_STATUSES}
                        placeholder={staffFull.length ? staffFull.map((i) => renderStaffName(i)).join(', ') : 'All'}
                        defaultValue={staffFull}
                        renderValue={(i) => renderStaffName(i)}
                        handleSelect={handlePersonSelect}
                        noError={true}
                    />
                    <CheckboxesTags
                        handleChange={(list) => setFilter(MAP_FILTER_KEYS.STAFF_STATUSES, list)}
                        name={MAP_FILTER_KEYS.STAFF_STATUSES}
                        renderValue={(i) => i?.name}
                        value={pickedStaffStatuses}
                        permissionsList={staffStatuses}
                        outLabel={'Staff Status'}
                        placeholder={
                            pickedStaffStatuses.length
                                ? namesOf(pickedStaffStatuses)
                                : defaultStatusLabel(staffStatuses, DEFAULT_STAFF_STATUSES)
                        }
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: '100%' }}
                    />
                    <SelectAutocompletePaginationMultiple
                        type={'client'}
                        name={MAP_FILTER_KEYS.CLIENTS}
                        outLabel={'Client'}
                        uiType={'tableFilter'}
                        defaultStatuses={clientStatusIds.length ? clientStatusIds : DEFAULT_CLIENT_STATUSES}
                        placeholder={clientFull.length ? clientFull.map((i) => renderClientName(i)).join(', ') : 'All'}
                        defaultValue={clientFull}
                        renderValue={(i) => renderClientName(i)}
                        handleSelect={handlePersonSelect}
                        noError={true}
                    />
                    <CheckboxesTags
                        handleChange={(list) => setFilter(MAP_FILTER_KEYS.CLIENT_STATUSES, list)}
                        name={MAP_FILTER_KEYS.CLIENT_STATUSES}
                        renderValue={(i) => i?.name}
                        value={pickedClientStatuses}
                        permissionsList={clientStatuses}
                        outLabel={'Client Status'}
                        placeholder={
                            pickedClientStatuses.length
                                ? namesOf(pickedClientStatuses)
                                : defaultStatusLabel(clientStatuses, DEFAULT_CLIENT_STATUSES)
                        }
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: '100%' }}
                    />
                    <CheckboxesTags
                        handleChange={(list) => setFilter(MAP_FILTER_KEYS.STATES, list)}
                        name={MAP_FILTER_KEYS.STATES}
                        renderValue={(i) => i?.name}
                        value={pickedStates}
                        permissionsList={stateOptions}
                        outLabel={'State'}
                        placeholder={pickedStates.length ? namesOf(pickedStates) : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: '100%' }}
                    />
                    <CheckboxesTags
                        handleChange={(list) => setFilter(MAP_FILTER_KEYS.CITIES, list)}
                        name={MAP_FILTER_KEYS.CITIES}
                        renderValue={(i) => i?.name}
                        value={pickedCities}
                        permissionsList={cityOptions}
                        outLabel={'City'}
                        placeholder={pickedCities.length ? namesOf(pickedCities) : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: '100%' }}
                    />
                </FilterPopover>
                <SelectedFilters
                    filtersList={chipFilters}
                    state={filters}
                    onSave={saveFilters}
                    resetParams={{}}
                    marginBottom={0}
                />
            </div>

            <div className="clients-staff-map__body">
                <div ref={containerRef} className="clients-staff-map__canvas" />

                <div className="clients-staff-map__legend">
                    {[
                        { type: MAP_MARKER_TYPES.CLIENT, label: `Client (${filteredClients.length})` },
                        { type: MAP_MARKER_TYPES.STAFF, label: `Staff (${filteredStaff.length})` },
                    ].map((entry) => (
                        <button
                            key={entry.type}
                            type="button"
                            className={`clients-staff-map__legend-item${visible[entry.type] ? '' : ' is-off'}`}
                            onClick={() => toggle(entry.type)}
                            title={visible[entry.type] ? 'Click to hide' : 'Click to show'}
                        >
                            <span className="clients-staff-map__legend-icon" style={{ background: MAP_COLORS[entry.type] }}>
                                {entry.type === MAP_MARKER_TYPES.CLIENT ? <ClientLegendGlyph /> : <StaffLegendGlyph />}
                            </span>
                            <span className="clients-staff-map__legend-label">{entry.label}</span>
                        </button>
                    ))}
                </div>

                {(loading || !googleReady) && (
                    <div className="clients-staff-map__overlay">
                        <Loader />
                    </div>
                )}

                {showEmpty && (
                    <div className="clients-staff-map__overlay">
                        <p className="clients-staff-map__empty">
                            {nothingLoaded ? 'No clients or staff have a mappable address yet.' : 'No clients or staff match these filters.'}
                        </p>
                    </div>
                )}

                {selectedStaff && (
                    <SelectActions
                        selectedList={assignedClients.length}
                        countLabel={`assigned client${assignedClients.length === 1 ? '' : 's'}`}
                        noSubmit
                        removeSelected={clearSelection}
                        renderOtherBtns={
                            <>
                                <button className="select-all-btn" onClick={handleShowAssignments}>
                                    <img className="csm-bar-icon" src={Images.manageNote} alt="" />
                                    <p>Show Assignments</p>
                                </button>
                                <button className="select-all-btn" onClick={clearSelection}>
                                    <img className="csm-bar-icon" src={Images.selectAll} alt="" />
                                    <p>Clear Selection</p>
                                </button>
                            </>
                        }
                    />
                )}
            </div>
        </div>
    );
};
